import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * What a tester sees when the interface breaks.
 *
 * Without one of these, a render that throws unmounts the whole tree and leaves
 * a white page: no message, no clue, nothing to report beyond "está en blanco".
 * That happened — an endpoint returned the wrong shape and the page vanished —
 * and the blankness cost more time than the bug did.
 *
 * A class, because an error boundary can only be a class: there is no hook for
 * componentDidCatch. It is the one place in this codebase that is not a
 * function component, and this comment is why.
 *
 * It shows what went wrong rather than a friendly nothing. The people using
 * this during a test round are trying to report bugs, and "algo salió mal" is
 * not a bug report.
 */
type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console for whoever has devtools open, with the component
    // stack that says which screen it was.
    console.error('Interface error', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-lg px-6 py-16">
        <p className="font-mono text-[10px] tracking-[0.14em] text-teal uppercase">
          Leadership Accelerator
        </p>
        <h1 className="mt-3 text-[22px] leading-tight font-extrabold tracking-[-0.02em]">
          Esta pantalla no se pudo mostrar
        </h1>
        <p className="mt-2 text-[14.5px] leading-relaxed text-soft">
          Es un fallo nuestro, no algo que hayas hecho mal. Tus respuestas no se
          han perdido: lo que ya habías guardado sigue guardado.
        </p>

        <div className="mt-4 rounded-xl border border-line bg-sand px-3.5 py-3">
          <p className="font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
            Para reportarlo
          </p>
          <p className="mt-1 font-mono text-[12px] break-all text-soft">
            {window.location.pathname}
          </p>
          <p className="mt-1 font-mono text-[12px] break-words text-soft">{error.message}</p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="cursor-pointer rounded-xl bg-teal px-4 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep"
          >
            Reintentar
          </button>
          <a
            href="/"
            className="rounded-xl border border-line bg-paper px-4 py-2.5 text-[13.5px] font-bold transition hover:border-teal"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }
}
